import { useEffect, useCallback } from 'react';
import { getUserStores } from '../api';

/**
 * Developer tools hook - provides secret commands for debugging
 *
 * Secret Commands:
 * - Ctrl+Shift+E: Export current user as simulator persona
 */
export function useDevTools() {
  const exportPersona = useCallback(async () => {
    console.log('\n🔧 Exporting persona...\n');

    // Get user data from localStorage
    const userDataRaw = localStorage.getItem('user_data');
    const accessToken = localStorage.getItem('access_token');

    if (!userDataRaw) {
      console.error('❌ No user data found. Please log in first.');
      alert('No user data found. Please log in first.');
      return;
    }

    let userData;
    try {
      userData = JSON.parse(userDataRaw);
    } catch (e) {
      console.error('❌ Failed to parse user data:', e);
      return;
    }

    // Decode token payload
    let tokenPayload = null;
    let hutbotUser = null;
    if (accessToken) {
      try {
        const base64Payload = accessToken.split('.')[1];
        tokenPayload = JSON.parse(atob(base64Payload));
        if (tokenPayload?.hutbot_user) {
          hutbotUser = JSON.parse(tokenPayload.hutbot_user);
        }
      } catch {
        console.warn('⚠️ Could not decode token payload');
      }
    }

    // Fetch stores data
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let storesData: any[] = [];
    try {
      console.log('📡 Fetching stores data...');
      const storesResponse = await getUserStores();
      if (storesResponse.status && storesResponse.data) {
        storesData = storesResponse.data.map((store) => ({
          id: parseInt(store.internalStoreNumber?.replace(/\D/g, '') || '0') || Math.floor(Math.random() * 100000),
          primaryBrand: store.brand || 'PH',
          countryIsoCode: store.country || 'USA',
          aisOrganizationId: store.aisOrganizationId || '',
          territoryId: store.territoryId || '',
          regionId: store.regionId || '',
          marketId: store.marketId || '',
          areaId: store.areaId || '',
          regionName: store.regionName || '',
          storeName: store.storeName || '',
          champsNumber: store.champsNumber || '',
          internalStoreNumber: store.internalStoreNumber || '',
          aisStoreId: store.aisStoreId || '',
          storeAddress: store.address?.street || '',
          storeCity: store.address?.city || '',
          businessUnitId: store.businessUnitId || '',
          timeZone: store.timezone || 'America/New_York',
          testStore: store.isTestStore || false,
          latitude: store.latitude || 0,
          longitude: store.longitude || 0,
          userRole: userData.roles?.[0] || 'User',
        }));
      }
    } catch (e) {
      console.warn('⚠️ Could not fetch stores:', e);
    }

    // Generate persona ID
    const role = (userData.roles?.[0] || 'user').toLowerCase().replace(/[^a-z0-9]/g, '-');
    const name = `${userData.firstName || 'unknown'}`.toLowerCase();
    const personaId = `${role}-${name}-${Date.now().toString(36)}`;

    // Build hutbot_user stores array
    const hutbotStores = storesData.map((s) => ({
      id: s.id,
      userRole: s.userRole,
    }));

    // Build the persona object
    const persona = {
      id: personaId,
      name: `${userData.firstName || 'Unknown'} ${userData.lastName || 'User'} - ${userData.roles?.join(', ') || 'No Role'}`,
      hutbot_user: hutbotUser || {
        id: parseInt(userData.yumId?.replace(/\D/g, '') || String(Date.now())),
        firstName: userData.firstName || 'Unknown',
        middleName: '',
        lastName: userData.lastName || 'User',
        operationalRole: userData.roles?.[0] || 'User',
        userTypeGroup: 'Store',
        userType: userData.roles?.[0]?.toUpperCase()?.replace(/\s+/g, '_') || 'USER',
        userPrimaryBrand: storesData[0]?.primaryBrand || 'PH',
        userCountryIsoCode: storesData[0]?.countryIsoCode || 'USA',
        userHierarchyBrands: [...new Set(storesData.map((s) => s.primaryBrand))],
        userRoleGroups: userData.roles || [],
        vendorTools: ['BYTE_CONNECT'],
        stores: hutbotStores,
        isTeamMember: false,
      },
      identities: tokenPayload?.identities || [
        {
          userId: userData.email || userData.yumId || 'unknown@yum.com',
          provider: 'yum',
        },
      ],
      scope: userData.permissions || tokenPayload?.scope || ['store:read', 'store:write'],
      stores: storesData,
      profile: {
        areaStores: storesData.length > 1
          ? storesData.map((s) => ({
              champsNumber: s.champsNumber,
              countryIsoCode: s.countryIsoCode,
              id: s.id,
              internalStoreNumber: s.internalStoreNumber,
              primaryBrand: s.primaryBrand,
              storeName: s.storeName,
              userRole: s.userRole,
            }))
          : [],
        firstName: userData.firstName || 'Unknown',
        id: parseInt(userData.yumId?.replace(/\D/g, '') || String(Date.now())),
        lastName: userData.lastName || 'User',
        middleName: '',
        operationalRole: userData.roles?.[0] || 'User',
        userPrimaryBrand: storesData[0]?.primaryBrand || 'PH',
        userBrandRoleGroups: {
          [storesData[0]?.primaryBrand || 'PH']: userData.roles || [],
        },
        vendorTools: ['BYTE_CONNECT'],
        yumId: userData.email || userData.yumId || 'unknown@yum.com',
        email: userData.email || 'unknown@yum.com',
      },
    };

    // Output
    const output = JSON.stringify(persona, null, 2);

    console.log('\n✅ Persona exported!\n');
    console.log('─'.repeat(60));
    console.log(output);
    console.log('─'.repeat(60));

    // Copy to clipboard
    try {
      await navigator.clipboard.writeText(output);
      console.log('\n✅ Copied to clipboard!');
      alert(`Persona exported and copied to clipboard!\n\nID: ${personaId}\nStores: ${storesData.length}`);
    } catch {
      console.log('\n⚠️ Could not copy to clipboard. Copy from console.');
      alert('Persona exported! Check the console (F12) to copy the JSON.');
    }

    return persona;
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Shift+E: Export persona
      if (e.ctrlKey && e.shiftKey && e.key === 'E') {
        e.preventDefault();
        exportPersona();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [exportPersona]);

  return { exportPersona };
}
